/**
 * Unit-propagation policies for scalar arithmetic. Each policy maps an
 * op call's operand units to the unit of its result, using the pure
 * algebra in `lib/units`. Policies apply at VALUE level — a fold's
 * evaluation or an aggregation kernel unwraps its operands' per-row
 * units (`unitOfCell`) and boxes a numeric result that earns a unit.
 * Operands with no unit are treated as the dimensionless identity for
 * multiplicative ops, and ignored for additive ops; a policy returns
 * `undefined` ("no unit info") when no operand carries a unit, and
 * drops the unit on any genuine conflict (the engine's silent-drop rule).
 */

import { dimensionless, divide, equals, multiply, type Unit } from '../../units/unit.ts';

/** Derive a result unit from an op call's operand units. */
export type UnitPolicy = (operands: readonly (Unit | undefined)[]) => Unit | undefined;

/** Product of operand units (`*`); unit-less operands act as the dimensionless identity. */
export const productUnit: UnitPolicy = (units) => {
  if (units.every((u) => u === undefined)) {
    return undefined;
  }
  return units.map((u) => u ?? dimensionless).reduce(multiply, dimensionless);
};

/** First operand divided by the rest (`/`); cancellation collapses to dimensionless. */
export const quotientUnit: UnitPolicy = (units) => {
  if (units.every((u) => u === undefined)) {
    return undefined;
  }
  const [first = dimensionless, ...rest] = units.map((u) => u ?? dimensionless);
  return rest.reduce(divide, first);
};

/** Additive ops (`+ - mod min max`): the shared unit if all unit-bearing operands agree, else dropped. */
export const additiveUnit: UnitPolicy = (units) => {
  const [first, ...rest] = units.filter((u): u is Unit => u !== undefined);
  if (first === undefined) {
    return undefined;
  }
  return rest.every((u) => equals(u, first)) ? first : undefined;
};

/** Unary ops (`abs neg round floor ceil`): the operand's unit, unchanged. */
export const preserveUnit: UnitPolicy = (units) => units[0];
